import { PropsWithChildren, useEffect } from 'react';
import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
  UnifiedThemeProvider,
} from '@backstage/theme';

/**
 * Howden digital brand palette (Digital brand guidelines, August 2024, p8-9).
 * Only these colours are approved. Navy and blue-grey are not Howden colours.
 */
export const howden = {
  cobaltBlue: '#0857c3',
  pistachio: '#e0ec89',
  teal: '#7ce0d3',
  mossGreen: '#173f35',
  gold: '#8d741e',
  rosewood: '#3c0f11',
  mustard: '#ffbf3f',
  poppy: '#f9423a',

  grey: '#3d3d3d',
  grey60: '#8b8b8b',
  grey10: '#ececec',
  grey5: '#f5f5f5',
  white: '#ffffff',
  black: '#000000',
};

/**
 * One of the colour themes from the guidelines (p10). Each is built from a
 * "connecting" pair (p8). Text colours were chosen by measured WCAG contrast:
 * every text pairing is 4.5:1 or better, and the nav indicator is 3:1 or
 * better against the bar.
 */
export type HowdenThemeSpec = {
  /** Backstage theme id. `howden` stays the default (cobalt). */
  id: string;
  title: string;
  /** Links, buttons and headings on white. */
  primary: string;
  onPrimary: string;
  accent: string;
  onAccent: string;
  navBg: string;
  navText: string;
  navIndicator: string;
  navHover: string;
  heroBg: string;
  heroText: string;
  ctaBg: string;
  ctaText: string;
  /** A light tint of the primary, for highlighted rows and columns. */
  tint: string;
  /** Light or dark. Each colour has one of each. */
  mode: 'light' | 'dark';
  /** The colour family, shared by its light and dark theme. */
  family: string;
  page: string;
  surface: string;
  subtle: string;
  border: string;
  ink: string;
  muted: string;
  idle: string;
};

/** The light neutrals: white page, Howden grey text. */
const LIGHT = {
  mode: 'light' as const,
  page: howden.white,
  surface: howden.white,
  subtle: howden.grey5,
  border: howden.grey10,
  ink: howden.grey,
  muted: '#5c5c5c', // grey at 80%, 6.9:1 on white (60% tint fails AA for text)
  idle: howden.grey60,
};

/**
 * The dark neutrals. The guidelines define light pages only, so these are
 * neutral near-blacks. The theme's approved colour stays on the bar and hero.
 * Measured on the surface: ink 14.1:1, muted 8.4:1, idle 4.9:1.
 */
const DARK = {
  mode: 'dark' as const,
  page: '#141414',
  surface: '#1e1e1e',
  subtle: '#272727',
  border: '#3a3a3a',
  ink: howden.grey10,
  muted: '#b8b8b8',
  idle: howden.grey60,
};

const DARK_HOVER = 'rgba(255, 255, 255, 0.12)';
const LIGHT_HOVER = 'rgba(0, 0, 0, 0.06)';

export const HOWDEN_THEMES: HowdenThemeSpec[] = [
  {
    // White on cobalt 6.6:1, black on teal 13.5:1 (cobalt on teal is 4.3:1).
    ...LIGHT,
    family: 'cobalt',
    id: 'howden',
    title: 'Cobalt blue',
    primary: howden.cobaltBlue,
    onPrimary: howden.white,
    accent: howden.teal,
    onAccent: howden.black,
    navBg: howden.cobaltBlue,
    navText: howden.white,
    navIndicator: howden.teal,
    navHover: DARK_HOVER,
    heroBg: howden.cobaltBlue,
    heroText: howden.white,
    ctaBg: howden.teal,
    ctaText: howden.black,
    tint: '#f0f5fc',
  },
  {
    // White on moss 11.7:1, moss on pistachio 9.2:1.
    ...LIGHT,
    family: 'moss',
    id: 'howden-moss',
    title: 'Moss green',
    primary: howden.mossGreen,
    onPrimary: howden.white,
    accent: howden.pistachio,
    onAccent: howden.mossGreen,
    navBg: howden.mossGreen,
    navText: howden.white,
    navIndicator: howden.pistachio,
    navHover: DARK_HOVER,
    heroBg: howden.mossGreen,
    heroText: howden.white,
    ctaBg: howden.pistachio,
    ctaText: howden.mossGreen,
    tint: '#eef3f1',
  },
  {
    // White on gold 4.5:1, black on mustard 12.8:1. Mustard on gold is only
    // 2.75:1, so the nav indicator is white.
    ...LIGHT,
    family: 'gold',
    id: 'howden-gold',
    title: 'Gold',
    primary: howden.gold,
    onPrimary: howden.white,
    accent: howden.mustard,
    onAccent: howden.black,
    navBg: howden.gold,
    navText: howden.white,
    navIndicator: howden.white,
    navHover: DARK_HOVER,
    heroBg: howden.gold,
    heroText: howden.white,
    ctaBg: howden.mustard,
    ctaText: howden.black,
    tint: '#f8f4e8',
  },
  {
    // White on rosewood 16.6:1, black on poppy 5.9:1. The guidelines ask for
    // rosewood to be used sparingly (p10).
    ...LIGHT,
    family: 'rosewood',
    id: 'howden-rosewood',
    title: 'Rosewood',
    primary: howden.rosewood,
    onPrimary: howden.white,
    accent: howden.poppy,
    onAccent: howden.black,
    navBg: howden.rosewood,
    navText: howden.white,
    navIndicator: howden.poppy,
    navHover: DARK_HOVER,
    heroBg: howden.rosewood,
    heroText: howden.white,
    ctaBg: howden.poppy,
    ctaText: howden.black,
    tint: '#f8eeee',
  },
  {
    // Grey on white 10.9:1, gold indicator on white 4.5:1, white on gold 4.5:1.
    ...LIGHT,
    family: 'white',
    id: 'howden-white',
    title: 'White',
    primary: howden.grey,
    onPrimary: howden.white,
    accent: howden.gold,
    onAccent: howden.white,
    navBg: howden.white,
    navText: howden.grey,
    navIndicator: howden.gold,
    navHover: LIGHT_HOVER,
    heroBg: howden.grey5,
    heroText: howden.grey,
    ctaBg: howden.grey,
    ctaText: howden.white,
    tint: howden.grey5,
  },
  {
    // Moss on pistachio 9.2:1, white on moss 11.7:1.
    ...LIGHT,
    family: 'pistachio',
    id: 'howden-pistachio',
    title: 'Pistachio',
    primary: howden.mossGreen,
    onPrimary: howden.white,
    accent: howden.pistachio,
    onAccent: howden.mossGreen,
    navBg: howden.pistachio,
    navText: howden.mossGreen,
    navIndicator: howden.mossGreen,
    navHover: LIGHT_HOVER,
    heroBg: howden.pistachio,
    heroText: howden.mossGreen,
    ctaBg: howden.mossGreen,
    ctaText: howden.white,
    tint: '#f7fae3',
  },
];

/**
 * The dark theme for each colour. On a near-black surface the theme colour
 * itself is too dark for text (cobalt is 2.5:1), so links, buttons and
 * headings use the accent, measured on #1e1e1e: teal 10.7:1, pistachio
 * 13.1:1, mustard 10.1:1, poppy 4.7:1, grey ink 14.1:1.
 */
const darkOf = (
  light: HowdenThemeSpec,
  overrides: Partial<HowdenThemeSpec>,
): HowdenThemeSpec => ({
  ...light,
  ...DARK,
  id: `${light.id}-dark`,
  title: `${light.title} dark`,
  primary: light.accent,
  onPrimary: light.onAccent,
  ctaBg: light.accent,
  ctaText: light.onAccent,
  navHover: DARK_HOVER,
  ...overrides,
});

const [cobalt, moss, gold, rosewood, white, pistachio] = HOWDEN_THEMES;
HOWDEN_THEMES.push(
  darkOf(cobalt, { tint: '#16263a' }),
  darkOf(moss, { tint: '#1c2a24' }),
  darkOf(gold, { tint: '#2a2617', navIndicator: howden.mustard }),
  darkOf(rosewood, { tint: '#2e1a1a' }),
  // White dark: a near-black bar with grey ink and the gold accent.
  darkOf(white, {
    primary: howden.grey10,
    onPrimary: howden.black,
    accent: howden.gold,
    onAccent: howden.white,
    navBg: '#1e1e1e',
    navText: howden.grey10,
    navIndicator: howden.mustard,
    heroBg: '#1e1e1e',
    heroText: howden.grey10,
    ctaBg: howden.grey10,
    ctaText: howden.black,
    tint: '#262626',
  }),
  // Pistachio dark: a moss bar with pistachio text and accents.
  darkOf(pistachio, {
    primary: howden.pistachio,
    onPrimary: howden.mossGreen,
    navBg: howden.mossGreen,
    navText: howden.pistachio,
    navIndicator: howden.pistachio,
    heroBg: howden.mossGreen,
    heroText: howden.pistachio,
    ctaBg: howden.pistachio,
    ctaText: howden.mossGreen,
    tint: '#1c2a24',
  }),
);

/** The six colour families, in picker order. */
export const THEME_FAMILIES = HOWDEN_THEMES.filter(t => t.mode === 'light').map(
  t => ({ family: t.family, title: t.title }),
);

export const themeFor = (family: string, mode: 'light' | 'dark') =>
  HOWDEN_THEMES.find(t => t.family === family && t.mode === mode);

export const DEFAULT_THEME = HOWDEN_THEMES[0];

/** CSS custom property names, set on <html> by the theme provider. */
const VARS: Record<
  keyof Omit<HowdenThemeSpec, 'id' | 'title' | 'mode' | 'family'>,
  string
> = {
  page: '--brand-page',
  surface: '--brand-surface',
  subtle: '--brand-subtle',
  border: '--brand-border',
  ink: '--brand-ink',
  muted: '--brand-muted',
  idle: '--brand-idle',
  primary: '--brand-primary',
  onPrimary: '--brand-on-primary',
  accent: '--brand-accent',
  onAccent: '--brand-on-accent',
  navBg: '--brand-nav-bg',
  navText: '--brand-nav-text',
  navIndicator: '--brand-nav-indicator',
  navHover: '--brand-nav-hover',
  heroBg: '--brand-hero-bg',
  heroText: '--brand-hero-text',
  ctaBg: '--brand-cta-bg',
  ctaText: '--brand-cta-text',
  tint: '--brand-tint',
};

const cssVar = (key: keyof typeof VARS) =>
  `var(${VARS[key]}, ${DEFAULT_THEME[key]})`;

/**
 * Brand tokens for component styles. The theme-dependent ones are CSS
 * variables, so a theme change repaints every page without rebuilding styles.
 * They work anywhere CSS accepts a colour, but not in MUI colour maths
 * (alpha, darken), which need the real values from HOWDEN_THEMES.
 */
export const brand = {
  primary: cssVar('primary'),
  onPrimary: cssVar('onPrimary'),
  accent: cssVar('accent'),
  onAccent: cssVar('onAccent'),
  navBg: cssVar('navBg'),
  navText: cssVar('navText'),
  navIndicator: cssVar('navIndicator'),
  navHover: cssVar('navHover'),
  heroBg: cssVar('heroBg'),
  heroText: cssVar('heroText'),
  ctaBg: cssVar('ctaBg'),
  ctaText: cssVar('ctaText'),
  tint: cssVar('tint'),
  ink: cssVar('ink'),
  muted: cssVar('muted'),
  idle: cssVar('idle'),
  rule: howden.gold,
  focus: cssVar('primary'),
  page: cssVar('page'),
  surface: cssVar('surface'),
  subtle: cssVar('subtle'),
  border: cssVar('border'),
};

const headingFontFamily = '"GT Ultra Median", Georgia, serif';
const bodyFontFamily = '"Aktiv Grotesk", Arial, sans-serif';

/** The MUI and Backstage theme for one colour theme. */
export const createHowdenTheme = (spec: HowdenThemeSpec) => {
  // Page headers use the hero colours, which are contrast-checked with their
  // text. A gradient into the accent put white text on teal (1.6:1).
  const page = (shape: string) =>
    genPageTheme({
      colors: [spec.heroBg, spec.heroBg],
      shape,
      options: { fontColor: spec.heroText },
    });
  return createUnifiedTheme({
    ...createBaseThemeOptions({
      palette: {
        ...(spec.mode === 'dark' ? palettes.dark : palettes.light),
        primary: {
          main: spec.primary,
          light: spec.accent,
          dark: spec.primary,
          contrastText: spec.onPrimary,
        },
        secondary: {
          main: spec.primary,
          light: spec.accent,
          dark: spec.primary,
          contrastText: spec.onPrimary,
        },
        error: { main: howden.poppy },
        warning: { main: howden.mustard },
        info: { main: spec.primary },
        success: { main: spec.primary },
        background: { default: spec.page, paper: spec.surface },
        text: { primary: spec.ink, secondary: spec.muted },
        navigation: {
          background: spec.navBg,
          indicator: spec.navIndicator,
          color: spec.navText,
          selectedColor: spec.navText,
        },
      },
    }),

    fontFamily: bodyFontFamily,
    defaultPageTheme: 'home',
    pageTheme: {
      home: page(shapes.wave),
      documentation: page(shapes.wave2),
      tool: page(shapes.round),
      service: page(shapes.wave),
      website: page(shapes.wave),
      library: page(shapes.wave),
      other: page(shapes.wave),
      app: page(shapes.round),
      apis: page(shapes.wave),
    },

    typography: {
      htmlFontSize: 16,
      fontFamily: bodyFontFamily,
      h1: {
        fontFamily: headingFontFamily,
        fontSize: 60,
        fontWeight: 500,
        marginBottom: 0,
      },
      h2: {
        fontFamily: headingFontFamily,
        fontSize: 44,
        fontWeight: 500,
        marginBottom: 0,
      },
      h3: {
        fontFamily: headingFontFamily,
        fontSize: 32,
        fontWeight: 500,
        marginBottom: 0,
      },
      h4: {
        fontFamily: headingFontFamily,
        fontSize: 24,
        fontWeight: 500,
        marginBottom: 0,
      },
      h5: {
        fontFamily: headingFontFamily,
        fontSize: 20,
        fontWeight: 500,
        marginBottom: 0,
      },
      h6: {
        fontFamily: bodyFontFamily,
        fontSize: 16,
        fontWeight: 600,
        marginBottom: 0,
      },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: spec.page,
            color: spec.ink,
            fontFamily: bodyFontFamily,
          },
          'h1, h2, h3, h4, h5': { fontFamily: headingFontFamily },
          a: { color: spec.primary, textDecorationColor: howden.gold },
          'a:hover': { color: spec.primary, textDecorationColor: spec.primary },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            minHeight: 40,
            padding: '8px 16px',
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 'none',
          },
          contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
          containedPrimary: {
            backgroundColor: spec.primary,
            color: spec.onPrimary,
            '&:hover': { backgroundColor: spec.accent, color: spec.onAccent },
          },
          containedSecondary: {
            backgroundColor: spec.accent,
            color: spec.onAccent,
            '&:hover': { backgroundColor: spec.primary, color: spec.onPrimary },
          },
          outlinedPrimary: {
            borderColor: spec.primary,
            color: spec.primary,
            '&:hover': {
              borderColor: spec.primary,
              backgroundColor: spec.accent,
              color: spec.onAccent,
            },
          },
          textPrimary: {
            color: spec.primary,
            '&:hover': { backgroundColor: spec.subtle },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: 2 },
          elevation1: {
            boxShadow: 'none',
            border: `1px solid ${spec.border}`,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 2,
            border: `1px solid ${spec.border}`,
            boxShadow: 'none',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 2, fontFamily: bodyFontFamily },
          colorPrimary: { backgroundColor: spec.accent, color: spec.onAccent },
          colorSecondary: {
            backgroundColor: spec.subtle,
            color: spec.primary,
          },
        },
      },
      MuiTab: {
        styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 700,
            color: spec.ink,
            backgroundColor: spec.subtle,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          body1: { fontFamily: bodyFontFamily, fontSize: 16, lineHeight: 1.5 },
          body2: { fontFamily: bodyFontFamily, fontSize: 14, lineHeight: 1.5 },
        },
      },
    },
  });
};

/** The default (cobalt) theme, kept for existing imports. */
export const howdenLightTheme = createHowdenTheme(DEFAULT_THEME);

const builtThemes = new Map<string, ReturnType<typeof createHowdenTheme>>();
const builtTheme = (spec: HowdenThemeSpec) => {
  if (!builtThemes.has(spec.id))
    builtThemes.set(spec.id, createHowdenTheme(spec));
  return builtThemes.get(spec.id)!;
};

/**
 * Applies a Howden colour theme: the MUI theme, a body class the BUI CSS
 * targets, and the brand CSS variables that component styles read.
 */
export function HowdenThemeProvider({
  children,
  spec = DEFAULT_THEME,
}: PropsWithChildren<{ spec?: HowdenThemeSpec }>) {
  useEffect(() => {
    const root = document.documentElement;
    document.body.classList.add('theme-howden');
    document.body.classList.toggle('theme-howden-dark', spec.mode === 'dark');
    document.documentElement.style.colorScheme = spec.mode;
    for (const [key, name] of Object.entries(VARS)) {
      root.style.setProperty(name, spec[key as keyof typeof VARS]);
    }
    return () => {
      document.body.classList.remove('theme-howden', 'theme-howden-dark');
    };
  }, [spec]);

  return (
    <UnifiedThemeProvider theme={builtTheme(spec)}>
      {children}
    </UnifiedThemeProvider>
  );
}
